import {
  AggregationModelEnum,
  CustomerPortalSessionData,
  FieldMappingSystemType,
} from '@monetize/types/app';
import { ApiQueryItem } from './queryUtils';
import { asQueryUtil, ComposeQueryType } from './utils';

/**
 * Keys for React Query.
 * There are multiple sub-keys here because this service spans multiple concerns.
 * In the future, we may want to split this service up into multiple services.
 *
 * @link https://tanstack.com/query/v4/docs/guides/query-keys
 * @link https://tkdodo.eu/blog/effective-react-query-keys
 */

/**
 * TABLE SEARCH
 * /api/{entity}/search?query={searchTerm}
 */
const SEARCH_BASE = ['search'] as const;

const TABLE_SEARCH_KEYS = {
  base: SEARCH_BASE,
  accounts: () => [...TABLE_SEARCH_KEYS.base, 'accounts'] as const,
};

export const TABLE_SEARCH = asQueryUtil({
  accountSearch: {
    list: {
      endpoint: `/api/accounts/search`,
      queryKey: [...SEARCH_BASE, 'accounts'] as const,
    },
  },
  productSearch: {
    list: {
      endpoint: `/api/products/search`,
      queryKey: [...SEARCH_BASE, 'products'] as const,
    },
  },
  offeringSearch: {
    list: {
      endpoint: `/api/offerings/search`,
      queryKey: [...SEARCH_BASE, 'offerings'] as const,
    },
  },
  bucketSearch: {
    list: {
      endpoint: `/api/buckets/search`,
      queryKey: [...SEARCH_BASE, 'buckets'] as const,
    },
  },
});

/**
 * SELECT DATA
 * Query keys for useSelectData (MCustomSelect). Defined in the API layer, not
 * alongside the hook, so services can invalidate these caches without depending
 * on a UI component module.
 */
export const getUseSelectDataQueryKey = (
  endpoint?: string | string[],
  queryParams?: ComposeQueryType,
) => {
  return queryParams
    ? (['useSelectData', endpoint, queryParams] as const)
    : (['useSelectData', endpoint] as const);
};

export const getUseSelectDataByIdQueryKey = (recordIds?: string | string[]) => {
  return ['useSelectData', 'selectedRecordId', recordIds] as const;
};

export const getUseSelectDataInfiniteQueryKey = (
  endpoint?: string | string[],
  queryParams?: ComposeQueryType,
) => {
  return queryParams
    ? (['useSelectData', 'infinite', endpoint, queryParams] as const)
    : (['useSelectData', 'infinite', endpoint] as const);
};

/**
 * ACCOUNT
 */
export const accountServiceQueryKeys = {
  /** Keys for all account actions and other actions nested under an account */
  accounts: {
    base: ['accounts'] as const,
    accountList: () =>
      [...accountServiceQueryKeys.accounts.base, 'list'] as const,
    accountDetail: (id: string) =>
      [...accountServiceQueryKeys.accounts.base, id] as const,
    accountDetailOverview: (id: string) =>
      [
        ...accountServiceQueryKeys.accounts.accountDetail(id),
        'overview',
      ] as const,
    accountHierarchyById: (id: string) =>
      [...accountServiceQueryKeys.accounts.base, 'hierarchy', id] as const,
    accountContacts: (accountId: string) =>
      [
        ...accountServiceQueryKeys.accounts.accountDetail(accountId),
        'contacts',
      ] as const,
    accountSubscriptionOverview: (accountId: string) =>
      [
        ...accountServiceQueryKeys.accounts.accountDetailOverview(accountId),
        'subscriptions',
      ] as const,
    invoiceList: (accountId: string) =>
      [
        ...accountServiceQueryKeys.accounts.base,
        accountId,
        'invoices',
        'list',
      ] as const,
    invoicePreview: (accountId: string, billGroupId: string) =>
      [
        ...accountServiceQueryKeys.accounts.base,
        accountId,
        'invoices',
        'preview',
        billGroupId,
      ] as const,
  },
  billGroups: {
    base: ['billGroup'] as const,
    billGroupByAccount: (accountId: string) =>
      [...accountServiceQueryKeys.billGroups.base, accountId, 'list'] as const,
    billGroupById: (id: string) =>
      [...accountServiceQueryKeys.billGroups.base, id] as const,
    billGroupStatsById: (id: string) =>
      [...accountServiceQueryKeys.billGroups.base, id, 'stats'] as const,
  },
  credits: {
    base: ['credit'] as const,
    creditListByAccount: (accountId: string) =>
      [...accountServiceQueryKeys.credits.base, 'list', accountId] as const,
    creditDetail: (id: string) =>
      [...accountServiceQueryKeys.credits.base, id] as const,
    htmlTemplate: (creditId: string) =>
      [...accountServiceQueryKeys.credits.base, creditId, 'html'] as const,
    voidCredit: (creditId: string) => [
      ...accountServiceQueryKeys.credits.base,
      'void',
      creditId,
    ],
  },
  creditNotes: {
    base: ['creditNotes'] as const,
    creditNoteById: (creditNoteId: string) => [
      ...accountServiceQueryKeys.creditNotes.base,
      creditNoteId,
    ],
    htmlTemplate: (creditNoteId: string) =>
      [
        ...accountServiceQueryKeys.creditNotes.base,
        creditNoteId,
        'html',
      ] as const,
    voidCreditNote: (creditNoteId: string) => [
      ...accountServiceQueryKeys.creditNotes.base,
      'void',
      creditNoteId,
    ],
  },
  payments: {
    base: ['payments'] as const,
    paymentByAccount: (accountId: string) =>
      [...accountServiceQueryKeys.payments.base, accountId, 'list'] as const,
    paymentById: (id: string) =>
      [...accountServiceQueryKeys.payments.base, id] as const,
    htmlTemplate: (paymentId: string) =>
      [...accountServiceQueryKeys.payments.base, paymentId, 'html'] as const,
  },
  usage: {
    base: ['usage'] as const,
    usageListBase: () =>
      [...accountServiceQueryKeys.usage.base, 'list'] as const,
    usageList: (params: {
      subscriptionId?: string;
      usageTypeIds?: string[];
      startTime: string;
      endTime: string;
    }) => [...accountServiceQueryKeys.usage.usageListBase(), params] as const,
    usageAggregateList: (params: {
      accountIds?: string[];
      subscriptionIds?: string[];
      usageTypeIds?: string[];
      startTime: string;
      endTime: string;
      pageSize?: number;
      nextPageToken?: string;
    }) =>
      [
        ...accountServiceQueryKeys.usage.usageListBase(),
        'aggregate',
        params,
      ] as const,
  },
  paymentMethods: {
    base: ['paymentMethods'] as const,
    paymentMethod: (id: string) =>
      [...accountServiceQueryKeys.paymentMethods.base, id] as const,
    paymentMethodList: (accountId: string) =>
      [
        ...accountServiceQueryKeys.paymentMethods.base,
        accountId,
        'list',
      ] as const,
  },
  refunds: {
    base: ['refunds'] as const,
    refundListByAccount: (accountId: string) =>
      [...accountServiceQueryKeys.refunds.base, 'list', accountId] as const,
    refundById: (id: string) =>
      [...accountServiceQueryKeys.refunds.base, id] as const,
    htmlTemplate: (id: string) =>
      [...accountServiceQueryKeys.refunds.base, id, 'html'] as const,
  },

  accountDocuments: {
    base: ['accountDocuments'] as const,
    accountDocumentByAccount: (accountId: string) =>
      [
        ...accountServiceQueryKeys.accountDocuments.base,
        'list',
        accountId,
      ] as const,
    accountDocumentById: (id: string) =>
      [...accountServiceQueryKeys.accountDocuments.base, id] as const,
    accountDocumentSignerStats: (id: string) =>
      [
        ...accountServiceQueryKeys.accountDocuments.base,
        id,
        'signerStats',
      ] as const,
    accountDocumentFile: (id: string, storageId: string) =>
      [
        ...accountServiceQueryKeys.accountDocuments.base,
        id,
        'attachments',
        storageId,
      ] as const,
  },
};

const queryKeysAccount: Required<
  Omit<ApiQueryItem, 'create' | 'delete' | 'upload'>
> = {
  list: {
    endpoint: `/api/accounts`,
    queryKey: accountServiceQueryKeys.accounts.accountList(),
    // byIdQueryKey - API response for list and detail is not the same
  },
  byId: {
    endpoint: (id: string) => `/api/accounts/${id}`,
    queryKey: (id: string) =>
      accountServiceQueryKeys.accounts.accountDetail(id),
  },
  update: {
    endpoint: (id: string) => `/api/accounts/${id}`,
    invalidateKeys: [
      accountServiceQueryKeys.accounts.accountList(),
      TABLE_SEARCH_KEYS.accounts(),
    ],
    setDataKey: (id: string) =>
      accountServiceQueryKeys.accounts.accountDetail(id),
    skipListUpdate: true,
  },
};

export const ACCOUNT_SERVICE_API = asQueryUtil({
  accounts: queryKeysAccount,
});

const queryKeysCreditNotes: ApiQueryItem = {
  byId: {
    endpoint: (id: string) => `/api/creditNotes/${id}`,
    queryKey: (id: string) =>
      accountServiceQueryKeys.creditNotes.creditNoteById(id),
  },
};

export const CREDIT_AND_CREDIT_NOTE_SERVICE_API = asQueryUtil({
  creditNoteService: queryKeysCreditNotes,
});

const queryKeysPayments: ApiQueryItem = {
  byId: {
    endpoint: (id: string) => `/api/payments/${id}`,
    queryKey: (id: string) => accountServiceQueryKeys.payments.paymentById(id),
  },
};

const queryKeysRefunds: ApiQueryItem = {
  byId: {
    endpoint: (id: string) => `/api/refunds/${id}`,
    queryKey: (id: string) => accountServiceQueryKeys.refunds.refundById(id),
  },
};

export const PAYMENT_AND_REFUND_SERVICE_API = asQueryUtil({
  payments: queryKeysPayments,
  refunds: queryKeysRefunds,
});

/**
 * CONTACT
 */
export const contactServiceQueryKeys = {
  base: ['contacts'] as const,
  contactList: () => [...contactServiceQueryKeys.base, 'list'] as const,
  contactDetail: (id: string) => [...contactServiceQueryKeys.base, id] as const,
};

export const contractServiceQueryKeys = {
  base: ['contracts'] as const,
  contractList: () => [...contractServiceQueryKeys.base, 'list'] as const,
  contractDetail: (id: string) =>
    [...contractServiceQueryKeys.base, id] as const,
  accountContractList: (accountId: string) =>
    [...contractServiceQueryKeys.base, accountId, 'list'] as const,
};

export const notesServiceQueryKeys = {
  base: ['notes'] as const,
  noteListById: (entityPath: string, id: string) =>
    [...notesServiceQueryKeys.base, 'list', entityPath, id] as const,
};

/**
 * SUBSCRIPTION
 */
export const subscriptionServiceQueryKeys = {
  base: ['subscriptions'] as const,
  subscriptionDetail: (id: string) =>
    [...subscriptionServiceQueryKeys.base, id] as const,
  subscriptionMRRAmount: (accountId: string, subscriptionId: string) =>
    [
      ...accountServiceQueryKeys.accounts.accountDetail(accountId),
      ...subscriptionServiceQueryKeys.subscriptionDetail(subscriptionId),
      'mrrAmount',
    ] as const,
  subscriptionARRAmount: (accountId: string, subscriptionId: string) =>
    [
      ...accountServiceQueryKeys.accounts.accountDetail(accountId),
      ...subscriptionServiceQueryKeys.subscriptionDetail(subscriptionId),
      'arrAmount',
    ] as const,
  subscriptionEstimate: (accountId: string, subscriptionId: string) =>
    [
      ...accountServiceQueryKeys.accounts.accountDetail(accountId),
      ...subscriptionServiceQueryKeys.subscriptionDetail(subscriptionId),
      'estimate',
    ] as const,
  subscriptionTotalSpend: (accountId: string, subscriptionId: string) =>
    [
      ...accountServiceQueryKeys.accounts.accountDetail(accountId),
      ...subscriptionServiceQueryKeys.subscriptionDetail(subscriptionId),
      'totalSpend',
    ] as const,
  subscriptionHistory: (accountId: string, subscriptionId: string) =>
    [
      ...accountServiceQueryKeys.accounts.accountDetail(accountId),
      ...subscriptionServiceQueryKeys.subscriptionDetail(subscriptionId),
      'history',
    ] as const,
  accountSubscriptionList: (accountId: string) =>
    [
      ...accountServiceQueryKeys.accounts.accountDetail(accountId),
      'subscriptions',
      'list',
    ] as const,
  subscriptionScheduledChanges: (id: string) =>
    [
      ...subscriptionServiceQueryKeys.subscriptionDetail(id),
      'scheduledChanges',
    ] as const,
  subscriptionUsageConsumptionBase: () =>
    [...subscriptionServiceQueryKeys.base, 'usageConsumption'] as const,
  subscriptionUsageConsumption: ({
    subscriptionId,
    productId,
    ...rest
  }: {
    subscriptionId: string;
    productId?: string;
    usageTypeIds?: string[];
    startTime?: string;
    endTime?: string;
    aggregationModel?: AggregationModelEnum;
  }) =>
    [
      ...subscriptionServiceQueryKeys.subscriptionUsageConsumptionBase(),
      subscriptionId,
      productId,
      rest,
    ] as const,
};

/**
 * REACT QUERY HELPER
 *
 * Map of helpers for React Query utility functions
 *
 */
const accountCredits: ApiQueryItem<{
  accountId: string;
  billGroupId?: string;
  // Some endpoints don't follow same pattern
  skipNestedEndpoint?: boolean;
}> = {
  list: {
    endpoint: (args) => `/api/accounts/${args?.accountId}/credits`,
    queryKey: (args) =>
      accountServiceQueryKeys.credits.creditListByAccount(args?.accountId),
    byIdQueryKey: (id: string) =>
      accountServiceQueryKeys.credits.creditDetail(id),
  },
  byId: {
    endpoint: (id: string) => `/api/credits/${id}`,
    queryKey: (id: string) => accountServiceQueryKeys.credits.creditDetail(id),
  },
  create: {
    endpoint: (args) =>
      `/api/accounts/${args?.accountId!}/billGroups/${args?.billGroupId!}/credits`,
    invalidateKeys: (id: string, args) => [
      accountServiceQueryKeys.credits.creditListByAccount(args?.accountId!),
    ],
    setDataKey: (id: string) =>
      accountServiceQueryKeys.credits.creditDetail(id),
  },
  update: {
    endpoint: (id, args) =>
      // activate/deactivate endpoints have a different URL pattern from PUT
      args?.skipNestedEndpoint
        ? `/api/credits/${id}` // used for actions (e.x. /api/credits/:creditId/deactivate)
        : `/api/accounts/${args?.accountId!}/billGroups/${args?.billGroupId!}/credits/${id}`,
    invalidateKeys: (id: string, args) => [
      accountServiceQueryKeys.credits.creditListByAccount(args?.accountId!),
    ],
    setDataKey: (id: string) =>
      accountServiceQueryKeys.credits.creditDetail(id),
  },
};

const accountPaymentMethods: ApiQueryItem<{
  accountId: string;
  paymentMethodId?: string;
  skipNestedEndpoint?: boolean;
}> = {
  list: {
    endpoint: (args) => `/api/accounts/${args?.accountId}/paymentMethods`,
    queryKey: (args) =>
      accountServiceQueryKeys.paymentMethods.paymentMethodList(args?.accountId),
    byIdQueryKey: (id: string) =>
      accountServiceQueryKeys.paymentMethods.paymentMethod(id),
  },
  byId: {
    endpoint: (id: string) => `/api/paymentMethods/${id}`,
    queryKey: (id: string) =>
      accountServiceQueryKeys.paymentMethods.paymentMethod(id),
  },
};

const billGroups: ApiQueryItem<{
  accountId: string;
}> = {
  list: {
    endpoint: () => `/api/v2/billGroups`,
    queryKey: (args) =>
      accountServiceQueryKeys.billGroups.billGroupByAccount(args?.accountId),
    byIdQueryKey: (id: string) =>
      accountServiceQueryKeys.billGroups.billGroupById(id),
  },
  byId: {
    endpoint: (id: string) => `/api/billGroups/${id}`,
    queryKey: (id: string) =>
      accountServiceQueryKeys.billGroups.billGroupById(id),
  },
  update: {
    endpoint: (id, args) =>
      `/api/accounts/${args?.accountId!}/billGroups/${id}`,
    invalidateKeys: (id: string, args) => [
      accountServiceQueryKeys.billGroups.billGroupByAccount(args?.accountId!),
    ],
    setDataKey: (id: string) =>
      accountServiceQueryKeys.billGroups.billGroupById(id),
  },
};

const contacts: ApiQueryItem<{ accountId?: string }> = {
  list: {
    endpoint: (args) => `/api/contacts`,
    queryKey: (args) => contactServiceQueryKeys.contactList(),
    byIdQueryKey: (id: string) => contactServiceQueryKeys.contactDetail(id),
  },
  byId: {
    endpoint: (id: string) => `/api/contacts/${id}`,
    queryKey: (id: string) => contactServiceQueryKeys.contactDetail(id),
  },
  create: {
    endpoint: (args) => `/api/contacts`,
    invalidateKeys: (id: string, args) => {
      const output = [contactServiceQueryKeys.contactList()];
      if (args?.accountId) {
        accountServiceQueryKeys.accounts.accountContacts(args.accountId);
      }
      return output;
    },
    setDataKey: (id: string) => contactServiceQueryKeys.contactDetail(id),
  },
  update: {
    endpoint: (id, args) => `/api/contacts/${id}`,
    invalidateKeys: (id: string, args) => {
      const output = [contactServiceQueryKeys.contactList()];
      if (args?.accountId) {
        accountServiceQueryKeys.accounts.accountContacts(args.accountId);
      }
      return output;
    },
    setDataKey: (id: string) => contactServiceQueryKeys.contactDetail(id),
  },
  delete: {
    endpoint: (id, args) => `/api/contacts/${id}`,
    invalidateKeys: (id: string, args) => {
      const output = [contactServiceQueryKeys.contactList()];
      if (args?.accountId) {
        accountServiceQueryKeys.accounts.accountContacts(args.accountId);
      }
      return output;
    },
    setDataKey: (id: string) => contactServiceQueryKeys.contactDetail(id),
  },
};

const contracts: ApiQueryItem = {
  list: {
    endpoint: () => `/api/contracts`,
    queryKey: () => contractServiceQueryKeys.contractList(),
    byIdQueryKey: (id: string) => contractServiceQueryKeys.contractDetail(id),
  },
  byId: {
    endpoint: (id: string) => `/api/contracts/${id}`,
    queryKey: (id: string) => contractServiceQueryKeys.contractDetail(id),
  },
};

const accountContracts: ApiQueryItem<{ accountId: string }> = {
  list: {
    endpoint: ({ accountId }) => `/api/accounts/${accountId}/contracts`,
    queryKey: ({ accountId }) =>
      contractServiceQueryKeys.accountContractList(accountId),
    byIdQueryKey: (id: string) => contractServiceQueryKeys.contractDetail(id),
  },
};

const accountSubscriptions: Required<
  Omit<ApiQueryItem, 'create' | 'delete' | 'upload'>
> = {
  // if we ever have a need to GET /api/subscriptions (across accounts) this account-scoped endpoint should be moved elsewhere
  list: {
    endpoint: (args) => `/api/accounts/${args?.accountId}/subscriptions`,
    queryKey: (args) => {
      return subscriptionServiceQueryKeys.accountSubscriptionList(
        args.accountId,
      );
    },
    // No `byIdQueryKey`: list rows are not the full subscription (no
    // `offering`, `locked` not set), and seeding the detail cache with them
    // rendered the overview from a row first — its Cancel Subscription button
    // showed for a locked subscription until the real detail replaced it.
  },
  byId: {
    endpoint: (id: string) => `/api/subscriptions/${id}`,
    queryKey: (id: string) =>
      subscriptionServiceQueryKeys.subscriptionDetail(id),
  },
  update: {
    endpoint: (id, args) =>
      // cancel endpoint has a different URL pattern from regular PUT
      args?.isCancelOrActivate
        ? `/api/billGroups/${args?.billGroupId}/subscriptions/${id}`
        : `/api/subscriptions/${id}`, // not used presently
    invalidateKeys: (subscriptionId: string, args) => {
      return [
        subscriptionServiceQueryKeys.accountSubscriptionList(args.accountId),
        subscriptionServiceQueryKeys.subscriptionDetail(subscriptionId),
      ];
    },
    setDataKey: (id: string) =>
      subscriptionServiceQueryKeys.subscriptionDetail(id),
    skipListUpdate: true,
  },
};

const accountDocuments: ApiQueryItem<{
  accountId: string;
}> = {
  list: {
    endpoint: (args) => `/api/accounts/${args?.accountId}/accountDocuments`,
    queryKey: (args) =>
      accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
        args?.accountId,
      ),
    byIdQueryKey: (id: string) =>
      accountServiceQueryKeys.accountDocuments.accountDocumentById(id),
  },
  byId: {
    endpoint: (id: string, args) => `/api/accountDocuments/${id}`,
    queryKey: (id: string, args) =>
      accountServiceQueryKeys.accountDocuments.accountDocumentById(id),
  },
  create: {
    endpoint: (args) => `/api/accounts/${args?.accountId!}/accountDocuments`,
    invalidateKeys: (id: string, args) => [
      accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
        args?.accountId!,
      ),
    ],
    setDataKey: (id: string) =>
      accountServiceQueryKeys.accountDocuments.accountDocumentById(id),
  },
  update: {
    endpoint: (id, args) =>
      `/api/accounts/${args?.accountId!}/accountDocuments/${id}`,
    invalidateKeys: (id: string, args) => [
      accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
        args?.accountId!,
      ),
    ],
    setDataKey: (id: string) =>
      accountServiceQueryKeys.accountDocuments.accountDocumentById(id),
  },
};

const paymentRefunds: ApiQueryItem = {
  byId: {
    endpoint: (id: string) => `/api/refunds/${id}`,
    queryKey: (id: string) => refundServiceQueryKeys.refundDetail(id),
  },
};

export const QUERY_UTILS_ENTRIES = asQueryUtil({
  accountContracts,
  accountCredits,
  accountPaymentMethods,
  accountSubscriptions,
  billGroups,
  contacts,
  contracts,
  paymentRefunds,
  accountDocuments,
});

export const fieldMappingServiceQueryKeys = {
  base: ['crmFieldMapping'] as const,
  crmFields: (system: FieldMappingSystemType) =>
    [...fieldMappingServiceQueryKeys.base, 'crmFields', system] as const,
  crmFieldMappings: (system: FieldMappingSystemType) =>
    [
      ...fieldMappingServiceQueryKeys.base,
      'crmFieldMappings',
      'userDefined',
      system,
    ] as const,
  accountingFields: (system: FieldMappingSystemType) =>
    [...fieldMappingServiceQueryKeys.base, 'accountingFields', system] as const,
  accountingFieldMappings: (system: FieldMappingSystemType) =>
    [
      ...fieldMappingServiceQueryKeys.base,
      'accountingFieldMappings',
      'userDefined',
      system,
    ] as const,
  monetizeNowAccountingFields: (system: FieldMappingSystemType) =>
    [
      ...fieldMappingServiceQueryKeys.base,
      'monetizeNowAccountingFields',
      system,
    ] as const,
};

export const priceUpliftServiceQueryKeys = {
  base: ['priceUplifts'] as const,
  priceUplift: (id: string) =>
    [...priceUpliftServiceQueryKeys.base, id] as const,
};

export const quoteOfferingRateServiceQueryKeys = {
  base: ['quoteOfferingRate'] as const,
  /** Prefix covering every rate's editability for a quote offering. */
  editabilityBase: (quoteId: string, quoteOfferingId: string) =>
    [
      ...quoteOfferingRateServiceQueryKeys.base,
      quoteId,
      quoteOfferingId,
      'editability',
    ] as const,
  /**
   * Keyed by rateId so swapping the offering's rate (catalog <-> account
   * specific, or cloning) is a distinct query rather than stale cached data.
   */
  editability: (quoteId: string, quoteOfferingId: string, rateId: string) =>
    [
      ...quoteOfferingRateServiceQueryKeys.editabilityBase(
        quoteId,
        quoteOfferingId,
      ),
      rateId,
    ] as const,
};

// REFUND
export const refundServiceQueryKeys = {
  base: ['refunds'] as const,
  refundDetail: (id: string) => [...refundServiceQueryKeys.base, id] as const,
};

export const getCustomerPortalKeys = (
  sessionData: CustomerPortalSessionData,
) => [
  sessionData.tenantId,
  sessionData.sessionId,
  sessionData.accountId,
  sessionData.billGroupId,
];

export const customerPortalServiceQueryKeys = {
  base: ['customerPortal'] as const,
  /**
   * AUTHENTICATED ROUTES
   */
  accountConfig: () =>
    [...customerPortalServiceQueryKeys.base, 'accountConfig'] as const,
  /**
   * ANONYMOUS ROUTES
   */
  data: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'data',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  companyInfo: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'companyInfo',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  invoices: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'invoices',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  invoicePdf: (sessionData: CustomerPortalSessionData, invoiceId: string) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'invoicePdf',
      invoiceId,
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  quotePdf: (sessionData: CustomerPortalSessionData, quoteId: string) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'quotePdf',
      quoteId,
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  paymentMethods: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'paymentMethods',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  payments: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'payments',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  subscriptionView: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'subscriptionView',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
  paymentConfiguration: (sessionData: CustomerPortalSessionData) =>
    [
      ...customerPortalServiceQueryKeys.base,
      'paymentConfiguration',
      ...getCustomerPortalKeys(sessionData),
    ] as const,
};
